import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download, FileText, ChevronRight } from 'lucide-react';
import { EAR_TYPE_LABELS } from './WorkflowEar';
import {
  EarFieldsEditor, diffEarFields,
  engagementToEarValues, earPrefillFields, EarTabBar, earShortName,
  copyEarFields, EarCopyRow, EarPerTabHint,
  EAR_SCREENING_SECTION_HINT, EAR_SCREENING_BLANK_NOTE, EAR_SCREENING_DOC_ONLY_HINT, earScreeningFromPayload,
  appendEarScreening, earScreeningGaps, earTabsSlotConflict, EarSlotConflictNotice,
} from './WorkflowEarNew';
import { ScreeningUpload, ENGREG_REVIEWER_HINT } from './WorkflowEngRegNew';
import { ScreeningAnswersView } from '../components/shared/ScreeningFields';
import { pieText } from '../utils/engagementProfile';
import RefLink from '../components/shared/RefLink';
import AttachmentsList from '../components/workflow/AttachmentsList';
import { SubSection } from '../components/workflow/FormShell';
import GroupedEngagementPicker from '../components/workflow/GroupedEngagementPicker';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import EngagementAttachments from '../components/shared/EngagementAttachments';
import { Field, FilePicker, FormModal, inputCls, btnGhost } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation, FieldInvalidInline } from '../components/shared/formValidation';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import ApprovalsCard from '../components/workflow/ApprovalsCard';
import ReviewerSelect, { useClearOffRosterPick, useIsQmReviewer, reviewerPickOf } from '../components/workflow/ReviewerSelect';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import { useFieldComments, FieldCommentsProvider, FieldCommentInline } from '../components/workflow/fieldComments';
import { fmtBytes, detailLabel, detailValue } from '../components/workflow/constants';
import { CoverageValue, fmtCoverageDefault, EngagementName, engagementLabelsOf } from '../components/workflow/CoverageDisplay';
import NowName, { StaffNames } from '../components/shared/NowName';
import { engagementDisplayName } from '../utils/engagementName';
import { qcEstimatedFeeOf } from './qcQuestions';
import { QcFormView } from '../components/workflow/QcForm';
import { LegacyImportBadge } from '../components/records/legacyImport';
import { formatDate, formatDateShort, formatDateTime } from '../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../utils/api';

// Engagement Amendment — detail page (docs/modules/workflow-amendment.md).
// UPDATE-ONLY (access belongs to the CWCC flow; the QC link is
// the QC flow's job — the engagement's current QC shows read-only).
const fmtDate = (v) => (v ? formatDate(v) : '—');
// The requester's own screening document (answer No) — shown in the Screening
// card, not with the supporting files.
const isScreeningDoc = (f) => f.docType === 'SCREENING' && !f.isAttachmentUpdate && !f.entityId;
const CONSOLIDATED_LABEL = { yes: 'Yes', no: 'No' };

const FIELD_LABELS = {
  clientGroup: 'Client Group', engagementName: 'Engagement Name', entityName: 'Entity Name',
  entityId: 'Entity Link', yearEnd: 'Year End (FYE)',
  partnerStaffIds: 'Partner in Charge', directorStaffIds: 'Director(s)', managerStaffIds: 'Manager(s)',
  partner: 'Partner', director: 'Director', manager: 'Manager', // legacy single-field rows
  eqr: 'EQR', serviceProvided: 'Service Provided', status: 'Status',
  remarks: 'Remarks', teamStaffIds: 'Team Members', isGroupAudit: 'Group Audit',
  underInvestigation: 'Client under investigation by regulatory authorities?',
  tcwgFeeCommunicated: 'Fee Information Communicated to TCWG',
  // Client profile — same labels as Registration's detail page.
  dateOfAppointment: 'Date of Appointment',
  descriptionOfService: 'Description of Service',
  ultimateParent: 'Ultimate Parent',
  overseasOperations: 'Overseas Operations',
  consolidatedFs: 'Consolidated FS',
  isPie: 'Public Interest Entity (PIE)?',
  pieType: 'Type of PIE',
  pieTypeOther: 'Type of PIE (if Others)',
  proposedFees: 'Estimated Fees',
  proposedFeesCcy: 'Estimated Fees Currency',
  ssicPrimary: 'SSIC Code Primary',
  ssicSecondary: 'SSIC Code Secondary',
  principalActivity1: 'Principal Activity 1',
  principalActivity2: 'Principal Activity 2',
  secRegistered: 'SEC Registered',
  // QM screening question.
  screeningRequired: 'QM Screening',
  // Add same-group companies — approval creates their engagements.
  addEntities: 'Add Companies (New Engagements on Approval)',
  // The request's one screening answer (all covered engagements).
  screening: 'Does QM need to perform screening?',
};

const fmtVal = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}(T|$)/.test(s) ? formatDate(s) : s;
};

const QC_FILE_LABEL = { QC_FORM: 'QC document', QC_PREV: 'Previous year QC' };

// The engagement's current acceptance QC in full (read-only context card).
const QcReviewCard = ({ qc, heading, downloadHref }) => {
  const [showForm, setShowForm] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">{heading}</p>
      {!qc ? (
        <p className="text-sm text-slate-500">—</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700">{qc.qcType}</span>
            {qc.requestNo && <span className="text-sm text-slate-800">{qc.requestNo}</span>}
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">{qc.submissionMethod === 'file' ? 'Uploaded QC document' : 'QC6 / QC7 form'}</span>
            {qc.legacyImport && <LegacyImportBadge />}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {qc.entityName}{qc.uen ? ` (${qc.uen})` : ''}{qc.clientGroup ? ` · ${qc.clientGroup}` : ''} · FYE {formatDateShort(qc.yearEnd)}
          </div>
          {qc.remarks && <div className="text-xs text-slate-500 mt-1 break-words"><span className="font-medium text-slate-600">Remarks:</span> {qc.remarks}</div>}
          {(qc.files || []).length > 0 && (
            <ul className="divide-y divide-slate-100 mt-2">
              {qc.files.map((f) => (
                <li key={f.id} className="flex items-center gap-2 py-1.5 text-sm">
                  <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                  <span className="truncate text-slate-700">{f.originalName}</span>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600 shrink-0">{QC_FILE_LABEL[f.docType] || 'Supporting'}</span>
                  <a href={downloadHref(f.id)} className="ml-auto shrink-0 inline-flex items-center gap-1 text-blue-600 hover:text-blue-800" title="Download">
                    <Download className="w-4 h-4" />
                  </a>
                </li>
              ))}
            </ul>
          )}
          {qc.submissionMethod === 'form' && (
            <div className="mt-2">
              <button type="button" onClick={() => setShowForm((v) => !v)} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-800">
                <ChevronRight className={`w-4 h-4 transition-transform ${showForm ? 'rotate-90' : ''}`} />
                QC6 / QC7 Form Answers
              </button>
              {/* The same sectioned tables the QC request detail shows
                  (QcFormView) — the covered engagements feed the header. */}
              {showForm && (
                <div className="mt-3">
                  <QcFormView qcType={qc.qcType} answers={qc.answers} engagements={qc.engagements || []} />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const WorkflowEarDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [currentEngagement, setCurrentEngagement] = useState(null);
  const [currentEngagements, setCurrentEngagements] = useState([]);
  const [engagementDetails, setEngagementDetails] = useState([]);
  // Engagement information 卡目前顯示哪個涵蓋案（逐案切換）。
  const [infoEngId, setInfoEngId] = useState(null);
  const [engagementAttachments, setEngagementAttachments] = useState([]);
  const [currentQc, setCurrentQc] = useState(null);
  const [canReview, setCanReview] = useState(false);
  const [review, setReview] = useState(null);
  const [screeningRequest, setScreeningRequest] = useState(null);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [engagementLock, setEngagementLock] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/workflow/ear/${id}`),
        fetchJSON(`/api/workflow/ear/${id}/events`),
      ]);
      setRequest(detail.request);
      setCurrentEngagement(detail.currentEngagement);
      setCurrentEngagements(detail.currentEngagements || []);
      setEngagementDetails(detail.engagementDetails || []);
      setInfoEngId((cur) => {
        const ids = (detail.currentEngagements || []).map((e) => e.id);
        return ids.includes(cur) ? cur : (ids[0] || null);
      });
      setEngagementAttachments(detail.engagementAttachments || []);
      setCurrentQc(detail.currentQc || null);
      setCanReview(detail.canReview);
      setReview(detail.review || null);
      setScreeningRequest(detail.screeningRequest || null);
      setCanModify(detail.canModify);
      setCanDelete(detail.canDelete);
      setEngagementLock(detail.engagementLock || null);
      setEvents(hist.data || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setActing(true);
    setError(null);
    try {
      const data = await fetchJSON(`/api/workflow/ear/${id}/approve`, {
        method: 'POST', body: JSON.stringify({}),
      });
      const parts = ['Request approved — the requester has been notified.'];
      if (data.applied?.ok) parts.push('Changes applied to the engagement.');
      else if (data.applied && data.applied.ok === false) {
        parts.push('Applying the change failed — a senior manager should update the engagement in Management.');
      }
      setNotice(parts.join(' '));
      await load();
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  const resubmit = async () => {
    setActing(true);
    setError(null);
    try {
      await fetchJSON(`/api/workflow/ear/${id}/resubmit`, { method: 'POST' });
      setNotice('Request re-submitted — the reviewer has been notified.');
      await load();
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  const hasAwaitingInfo = request && request.status === 'awaiting_info';
  const DOC_LABELS = { FS: 'Signed Financial Statements', QC35: 'QC35 Form', GROUP_STRUCTURE: 'Group Structure', SUPPLEMENTARY: 'Supplementary', BIZFILE: 'BizFile', SCREENING: 'Screening Document' };
  const attachmentChanges = request ? (request.files || []).filter(f => f.isAttachmentUpdate) : [];
  const isLegacyAccess = request?.requestType === 'access';

  // 逐案修改：一張單多個案、各自的 payload；舊單退回單案鏡射。
  const amendments = request
    ? ((request.amendments && request.amendments.length > 0)
      ? request.amendments
      : [{
          engagementId: request.engagementId, engagementName: request.engagementName,
          serviceDescription: request.serviceDescription, payload: request.payload || {},
        }])
    : [];
  const multi = amendments.length > 1;
  // 公司名；同公司多案時加服務／描述（快照名為準，描述取現況）。
  const amendLabels = engagementLabelsOf(amendments.map((a) => ({
    ...(currentEngagements.find((e) => e.id === a.engagementId) || {}),
    engagementName: a.engagementName,
    id: a.engagementId,
  })));
  const amendNameOf = (a) => (amendLabels[a.engagementId] || {}).text || earShortName(a.engagementName);
  const infoLabels = engagementLabelsOf(currentEngagements);
  // History／退回差異卡／逐項意見的欄位對照：多案事件的 key 是
  // `<engagementId>:<field>`（後端不存顯示名），這裡用涵蓋名單長出「公司 —
  // 欄位」的對照表；單案素 key 照舊。
  const MULTI_LABELS = useMemo(() => {
    const out = { ...FIELD_LABELS };
    if (multi) {
      for (const a of amendments) {
        for (const [k, l] of Object.entries(FIELD_LABELS)) {
          out[`${a.engagementId}:${k}`] = `${amendNameOf(a)} — ${l}`;
        }
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  // 退件的逐項意見：能退件的人就能逐格留言，被退回的人就讀得到。
  const canComment = !!request && (!isLegacyAccess && !!review?.canRejectOnly && request.status === 'requested');
  const fc = useFieldComments({
    events,
    canComment,
    returned: !!request && request.status === 'awaiting_info',
    labels: MULTI_LABELS,
    amendment: true,
  });

  return (
    <RequestDetailShell
      backTo="/workflow/ear"
      backLabel="Engagement Amendment"
      loading={!request}
      error={error}
      notice={notice}
    >
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            // 多案標頭講「專案」不是第一案的組合長名（不是
            // 「Entity - UEN - FYE… +2 more」一長串）：集團 · FYE · N 案，
            // 公司名各自成 chip。單案照舊。
            typeLabel={multi
              ? [
                  EAR_TYPE_LABELS[request.requestType] || 'Engagement Change',
                  currentEngagement?.clientGroup || earShortName(request.engagementName),
                  currentEngagement?.yearEnd ? `FYE ${formatDateShort(currentEngagement.yearEnd)}` : null,
                  `${amendments.length} engagements`,
                ].filter(Boolean).join(' · ')
              : `${EAR_TYPE_LABELS[request.requestType] || 'Engagement Change'} · ${engagementDisplayName(request.engagementName, request.serviceDescription)}`}
            chips={multi ? (
              <span className="flex flex-wrap items-center gap-1">
                {amendments.map((a) => (
                  <span
                    key={a.engagementId || a.engagementName}
                    title={a.engagementName}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 break-words"
                  >
                    {amendNameOf(a)}
                  </span>
                ))}
              </span>
            ) : null}
            badge={<WorkflowStatusBadge status={request.status} />}
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          {isLegacyAccess && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              Access-type amendments are retired — CaseWare access and charge codes now go through the CaseWare, Charge Code &amp; Team Update flow. This request is read-only.
            </div>
          )}

          <StatusActionsCard
            lock={engagementLock}
            sides={[{
              key: 'main', label: null, status: request.status,
              note: request.statusUpdatedAt ? formatDateTime(request.statusUpdatedAt) : null,
            }]}
            busy={acting}
            events={events}
            onApprove={!isLegacyAccess && canReview && request.status === 'requested' ? approve : null}
            approveLabel={() => 'Approve & Apply'}
            onReject={canComment ? () => setRejecting(true) : null}
            onModify={!isLegacyAccess && canModify ? () => setModifying(true) : null}
            onResubmit={!isLegacyAccess && canModify && hasAwaitingInfo ? resubmit : null}
            onDelete={canDelete ? () => setConfirmingDelete(true) : null}
          >
            <div className="min-w-0">
              <p className={detailLabel}>Reason</p>
              <p className={detailValue}>{request.reason || '—'}</p>
            </div>
          </StatusActionsCard>

          <ChangesSinceReturnCard
            events={events}
            amendment
            changeLabels={MULTI_LABELS}
            fmtChangeValue={fmtCoverageDefault}
          />

          <ApprovalsCard review={review} approvals={request.approvals || []} status={request.status} />

          {(() => {
            // 多案的資訊卡逐案切換（藥丸頁籤，與申請表單同一顆），各案
            // 的欄位現況、QC 與附件都跟著換；單案照舊。逐項意見的欄位 key 跟
            // 著加案前綴，意見才會指向正確的案。
            const infoEng = currentEngagements.find((e) => e.id === infoEngId) || currentEngagement;
            if (!infoEng) return null;
            const infoDetail = engagementDetails.find((d) => d.engagementId === infoEng.id) || null;
            const infoQc = infoDetail ? infoDetail.qc : currentQc;
            const infoAtt = infoDetail ? infoDetail.attachments : engagementAttachments;
            const cf = (field) => (multi ? `${infoEng.id}:${field}` : field);
            return (
            <SectionCard title="Engagement Information">
              {multi && (
                <EarTabBar
                  className="mb-4"
                  items={currentEngagements.map((e) => ({
                    id: e.id, label: (infoLabels[e.id] || {}).label || earShortName(e.engagementName), sub: (infoLabels[e.id] || {}).sub, title: e.engagementName,
                  }))}
                  activeId={infoEng.id}
                  onSelect={setInfoEngId}
                />
              )}
              <DetailGrid>
                <div className="sm:col-span-2">
                  <DetailItem label="Engagement"><EngagementName name={infoEng.engagementName} now={infoEng.engagementNameNow} serviceDescription={infoEng.serviceDescription} /></DetailItem>
                </div>
                <DetailItem label="Entity Name" field={cf('entityName')}><NowName value={infoEng.entityName} now={infoEng.entityNameNow} /></DetailItem>
                <DetailItem label="UEN"><NowName value={infoEng.uen} now={infoEng.uenNow} /></DetailItem>
                <DetailItem label="Client Group" field={cf('clientGroup')}><NowName value={infoEng.clientGroup} now={infoEng.clientGroupNow} /></DetailItem>
                <DetailItem label="Year End (FYE)" field={cf('yearEnd')}>{fmtDate(infoEng.yearEnd)}</DetailItem>
                <DetailItem label="Status" field={cf('status')}>{infoEng.status || '—'}</DetailItem>
                <DetailItem label="Service Provided" field={cf('serviceProvided')}>{infoEng.serviceProvided || '—'}</DetailItem>
                {/* Client profile — the reviewer sees the same
                    questions Registration asked, since they are amendable here. */}
                <DetailItem label="Public Interest Entity (PIE)" field={cf('isPie')}>{pieText(infoEng) || '—'}</DetailItem>
                {/* 以 QC 為準：涵蓋案的 QC6/QC7 一核准，費用就顯示 QC 表上的
                    數字（含幣別）；還沒有 QC 才顯示 engagement 自己的值。 */}
                <DetailItem label="Estimated Fees" field={cf('proposedFees')}>
                  {(() => {
                    const qcFee = infoQc ? qcEstimatedFeeOf(infoQc.answers, infoQc.qcType, { engagementId: infoEng.id, covered: infoQc.engagements || [] }) : null;
                    if (qcFee) {
                      const scope = qcFee.scope === 'group' ? `, group total of ${qcFee.coveredCount} engagements` : '';
                      return `${qcFee.currency} ${qcFee.amount.toLocaleString()} (from ${infoQc.qcType}${scope})`;
                    }
                    if (infoEng.proposedFees == null) return '—';
                    const shared = infoEng.proposedFeesShared && (infoEng.proposedFeesGroup || []).length > 1
                      ? ` (group total of ${infoEng.proposedFeesGroup.length} engagements)` : '';
                    return `${infoEng.proposedFeesCcy || 'SGD'} ${Number(infoEng.proposedFees).toLocaleString()}${shared}`;
                  })()}
                </DetailItem>
                <DetailItem label="Description of Service" field={cf('descriptionOfService')}>{infoEng.descriptionOfService || '—'}</DetailItem>
                <DetailItem label="Ultimate Parent" field={cf('ultimateParent')}>{infoEng.ultimateParent || '—'}</DetailItem>
                <DetailItem label="SSIC Code Primary" field={cf('ssicPrimary')}>{infoEng.ssicPrimary || '—'}</DetailItem>
                <DetailItem label="SSIC Code Secondary" field={cf('ssicSecondary')}>{infoEng.ssicSecondary || '—'}</DetailItem>
                <DetailItem label="Principal Activity 1" field={cf('principalActivity1')}>{infoEng.principalActivity1 || '—'}</DetailItem>
                <DetailItem label="Principal Activity 2" field={cf('principalActivity2')}>{infoEng.principalActivity2 || '—'}</DetailItem>
                <DetailItem label="Overseas Operations" field={cf('overseasOperations')}>{fmtVal(infoEng.overseasOperations)}</DetailItem>
                <DetailItem label="Consolidated FS" field={cf('consolidatedFs')}>{CONSOLIDATED_LABEL[infoEng.consolidatedFs] || '—'}</DetailItem>
                <DetailItem label="SEC Registered" field={cf('secRegistered')}>{fmtVal(infoEng.secRegistered)}</DetailItem>
                <DetailItem label="Group Audit" field={cf('isGroupAudit')}>{fmtVal(infoEng.isGroupAudit)}</DetailItem>
                <DetailItem label="Client under investigation by regulatory authorities?" field={cf('underInvestigation')}>{fmtVal(infoEng.underInvestigation)}</DetailItem>
                <DetailItem label="Fee Information Communicated to TCWG" field={cf('tcwgFeeCommunicated')}>{fmtVal(infoEng.tcwgFeeCommunicated)}</DetailItem>
                <DetailItem label="Does QM need to perform screening?" field={cf('screeningRequired')}>{fmtVal(infoEng.screeningRequired)}</DetailItem>
                <DetailItem label="Partner in Charge" field={cf('partnerStaffIds')}>{(infoEng.partners || []).length > 0
                  ? <StaffNames list={infoEng.partners} />
                  : <NowName value={infoEng.partner} now={infoEng.partnerNow} />}</DetailItem>
                <DetailItem label="Director(s)" field={cf('directorStaffIds')}>{(infoEng.directors || []).length > 0
                  ? <StaffNames list={infoEng.directors} />
                  : <NowName value={infoEng.director} now={infoEng.directorNow} />}</DetailItem>
                <DetailItem label="Manager(s)" field={cf('managerStaffIds')}>{(infoEng.managers || []).length > 0
                  ? <StaffNames list={infoEng.managers} />
                  : <NowName value={infoEng.manager} now={infoEng.managerNow} />}</DetailItem>
                <DetailItem label="EQR (Assigned by QC6 / QC7)">{infoEng.eqr || '—'}</DetailItem>
                <DetailItem label="Transnational Audit (TNA) (Set by QC6 / QC7)">{fmtVal(infoEng.isTna)}</DetailItem>
                <DetailItem label="Transnational Entity (TNE) (Set by QC6 / QC7)">{fmtVal(infoEng.isTne)}</DetailItem>
                <DetailItem label="Listed Company (Set by QC6 / QC7)">{fmtVal(infoEng.isListed)}</DetailItem>
                <DetailItem label="CaseWare">{infoEng.casewareOpened ? 'Activated' : 'Not activated'}</DetailItem>
                <DetailItem label="Charge Code">{infoEng.chargeCodeAssigned ? 'Activated' : 'Not activated'}</DetailItem>
                <div className="sm:col-span-2">
                  <DetailItem label="Team Members" field={cf('teamStaffIds')}>
                    <StaffNames list={infoEng.teamMembers} />
                  </DetailItem>
                </div>
              </DetailGrid>

              <div className="mt-5 pt-4 border-t border-slate-100">
                <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Current QC6 / QC7 Form</h3>
                {infoQc ? (
                  <QcReviewCard
                    qc={infoQc}
                    heading={`${infoQc.qcType} — ${infoQc.qcType === 'QC6' ? 'new client' : 'continuance'}`}
                    downloadHref={(fileId) => `${API_BASE_URL}/api/workflow/ear/${request.id}/qc-files/${infoQc.id}/${fileId}`}
                  />
                ) : (
                  <p className="text-sm text-slate-500">No QC6 / QC7 form is linked to this engagement yet — file one through the QC6 / QC7 Forms flow.</p>
                )}
              </div>

              <div className="mt-5 pt-4 border-t border-slate-100">
                <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Engagement Attachments</h3>
                <EngagementAttachments
                  attachments={infoAtt}
                  downloadHref={(f) => `${API_BASE_URL}/api/workflow/ear/${request.id}/engagement/${infoEng.id}/files/${f.source}/${f.fileId}`}
                />
              </div>
            </SectionCard>
            );
          })()}

          <SectionCard title="Requested Changes">
            {/* 一張單多個案、各自的修改——逐案分段，審核者一路看完整包
                再按一顆 Approve（不用 tab，藏在頁籤裡的變更審不到）。 */}
            <table className="w-full table-fixed text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500">
                  <th className="px-3 py-1.5 font-medium w-1/3">Field</th>
                  <th className="px-3 py-1.5 font-medium w-1/3">Current</th>
                  <th className="px-3 py-1.5 font-medium w-1/3">Requested</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {amendments.map((a) => {
                  const live = (currentEngagements || []).find((e) => e.id === a.engagementId)
                    || (a.engagementId === request.engagementId ? currentEngagement : null);
                  const entries = Object.entries(a.payload || {}).filter(([field]) => field !== 'addEntities' && field !== 'screening');
                  const attRows = attachmentChanges.filter(
                    (f) => (f.targetEngagementId || amendments[0].engagementId) === a.engagementId
                  );
                  if (entries.length === 0 && attRows.length === 0 && multi) {
                    return (
                      <React.Fragment key={a.engagementId || a.engagementName}>
                        <tr className="bg-slate-50">
                          <td colSpan={3} className="px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase break-words" title={a.engagementName}>{amendNameOf(a)}</td>
                        </tr>
                        <tr><td colSpan={3} className="px-3 py-2 text-slate-500">No changes on this engagement.</td></tr>
                      </React.Fragment>
                    );
                  }
                  return (
                    <React.Fragment key={a.engagementId || a.engagementName}>
                      {multi && (
                        <tr className="bg-slate-50">
                          <td colSpan={3} className="px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase break-words" title={a.engagementName}>{amendNameOf(a)}</td>
                        </tr>
                      )}
                      {entries.map(([field, value]) => {
                        const before = value?.before;
                        const after = value?.after;
                        const liveValue = live ? live[field] : undefined;
                        const rosterField = ['teamStaffIds', 'partnerStaffIds', 'directorStaffIds', 'managerStaffIds'].includes(field);
                        const drifted = !rosterField && live && request.status === 'requested'
                          && String(fmtVal(liveValue)) !== String(fmtVal(before));
                        return (
                          <tr key={`${a.engagementId}-${field}`}>
                            <td className="px-3 py-1.5 text-slate-700 align-top break-words">{FIELD_LABELS[field] || field}</td>
                            <td className="px-3 py-1.5 text-slate-500 align-top break-words">
                              {fmtVal(before)}
                              {drifted && (
                                <span className="block text-xs text-amber-600">
                                  now: {fmtVal(liveValue)} (changed since submission)
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-slate-900 font-medium align-top break-words">{fmtVal(after)}</td>
                          </tr>
                        );
                      })}
                      {attRows.map((f) => {
                        const tgt = f.replaces
                          ? engagementAttachments.find((x) => x.source === f.replaces.source && x.fileId === f.replaces.fileId)
                          : null;
                        return (
                          <tr key={`att-${f.id}`}>
                            <td className="px-3 py-1.5 text-slate-700 align-top break-words">
                              {DOC_LABELS[f.docType] || f.docType || 'Attachment'}
                            </td>
                            <td className="px-3 py-1.5 text-slate-500 align-top break-words">{tgt ? tgt.originalName : '—'}</td>
                            <td className="px-3 py-1.5 text-slate-900 font-medium align-top break-words">
                              <span className="inline-flex items-center gap-1 max-w-full">
                                <FileText className="w-3.5 h-3.5 text-slate-400" />
                                {f.originalName}
                                <a href={`${API_BASE_URL}/api/workflow/ear/${request.id}/files/${f.id}`}
                                target="_blank"
                                rel="noreferrer" className="text-blue-600 hover:text-blue-800 ml-1">
                                  <Download className="w-3.5 h-3.5" />
                                </a>
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                {/* 加公司——專案層訴求，不屬於任何一個頁籤。 */}
                {request.payload?.addEntities && (
                  <tr>
                    <td className="px-3 py-1.5 text-slate-700 align-top break-words">{FIELD_LABELS.addEntities}</td>
                    <td className="px-3 py-1.5 text-slate-500 align-top break-words">—</td>
                    <td className="px-3 py-1.5 text-slate-900 font-medium align-top break-words"><CoverageValue value={request.payload.addEntities.after} /></td>
                  </tr>
                )}
                {request.payload?.screening && (
                  <tr>
                    <td className="px-3 py-1.5 text-slate-700 align-top break-words">{FIELD_LABELS.screening}</td>
                    <td className="px-3 py-1.5 text-slate-500 align-top break-words">—</td>
                    <td className="px-3 py-1.5 text-slate-900 font-medium align-top break-words">{request.payload.screening.after}</td>
                  </tr>
                )}
                {amendments.every((a) => Object.keys(a.payload || {}).filter((k) => k !== 'addEntities' && k !== 'screening').length === 0)
                  && attachmentChanges.length === 0 && !request.payload?.addEntities && !request.payload?.screening && (
                  <tr><td colSpan={3} className="px-3 py-2 text-slate-500">No changes requested.</td></tr>
                )}
              </tbody>
            </table>
          </SectionCard>

          {/* The request's one screening answer — Yes shows the six answers QM
              works from (approval opens a Screening request); No shows the
              requester's own document, filed on every engagement at approval;
              under a reviewer outside QM there is no answer, only the document. */}
          {request.payload?.screening && (
            <SectionCard title="Screening">
              <p className="text-xs text-slate-500 mb-2">
                {typeof request.payload.screening.required === 'boolean' ? EAR_SCREENING_SECTION_HINT : EAR_SCREENING_DOC_ONLY_HINT}
              </p>
              {typeof request.payload.screening.required === 'boolean' && (
                <DetailGrid>
                  <DetailItem label="Does QM need to perform screening?" field="screeningRequired">
                    {request.payload.screening.required ? 'Yes' : 'No'}
                  </DetailItem>
                </DetailGrid>
              )}
              {request.payload.screening.required ? (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <ScreeningAnswersView r={request.payload.screening.answers || {}} />
                  <p className="text-sm text-slate-500 mt-2">
                    {screeningRequest
                      ? <>QM performs the screening in <RefLink to={`/workflow/screening/${screeningRequest.id}`} className="text-blue-600 underline">{screeningRequest.requestNo}</RefLink>.</>
                      : 'QM performs the screening in a Screening request opened when this amendment is approved.'}
                  </p>
                </div>
              ) : (
                <div className="mt-3">
                  <AttachmentsList
                    files={(request.files || []).filter(isScreeningDoc)}
                    downloadHref={(fl) => `${API_BASE_URL}/api/workflow/ear/${request.id}/files/${fl.id}`}
                    emptyText="No screening document attached."
                  />
                </div>
              )}
            </SectionCard>
          )}

          <SectionCard title="Attachments">
            {(request.files || []).filter((f) => !isScreeningDoc(f)).length === 0 ? (
              <p className="text-sm text-slate-500">No files attached.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {request.files.filter((f) => !isScreeningDoc(f)).map(f => {
                  const tgt = f.replaces
                    ? engagementAttachments.find(a => a.source === f.replaces.source && a.fileId === f.replaces.fileId)
                    : null;
                  return (
                    <li key={f.id} className="flex items-center justify-between py-2 gap-2">
                      <span className="flex flex-wrap items-center gap-2 text-sm text-slate-700 min-w-0">
                        <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="truncate">{f.originalName}</span>
                        {f.isAttachmentUpdate && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700">
                            Attachment update{f.docType ? ` · ${DOC_LABELS[f.docType] || f.docType}` : ''}
                          </span>
                        )}
                        {f.isAttachmentUpdate && (
                          <span className="text-xs text-slate-500">
                            {f.replaces ? `replaces ${tgt?.originalName || `${f.replaces.source} #${f.replaces.fileId}`}` : 'add new'}
                          </span>
                        )}
                        <span className="text-xs text-slate-500">{fmtBytes(f.sizeBytes)}</span>
                      </span>
                      <a
                        href={`${API_BASE_URL}/api/workflow/ear/${request.id}/files/${f.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 shrink-0"
                      >
                        <Download className="w-4 h-4" /> Download
                      </a>
                    </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>

          <HistoryCard events={events} changeLabels={MULTI_LABELS} fmtChangeValue={fmtCoverageDefault} />

          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => {
                await fetchJSON(`/api/workflow/ear/${request.id}/reject`, {
                  method: 'POST',
                  body: JSON.stringify({ reason, fieldComments }),
                });
                setRejecting(false);
                fc.clear();
                setNotice('Returned to the requester for changes.');
                load();
              }}
            />
          )}
          {modifying && (
            <EarModifyModal
              request={request}
              events={events}
              currentEngagements={currentEngagements.length > 0 ? currentEngagements : (currentEngagement ? [currentEngagement] : [])}
              onClose={() => setModifying(false)}
              onDone={() => {
                setModifying(false);
                setNotice(hasAwaitingInfo
                  ? 'Changes saved. Press Re-submit to send the request back for review.'
                  : 'Changes saved.');
                load();
              }}
            />
          )}
          {confirmingDelete && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message="This permanently deletes the request and its attachments. This cannot be undone."
              onClose={() => setConfirmingDelete(false)}
              onConfirm={async () => {
                await fetchJSON(`/api/workflow/ear/${request.id}`, { method: 'DELETE' });
                navigate('/workflow/ear');
              }}
            />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

// Requester modification — the same field editor as the new-request page,
// prefilled with the live engagements + the requested afters. 一張單多
// 個案就多個頁籤（各自修改），涵蓋集合也可換（未核准前都是草稿）。
const EarModifyModal = ({ request, events = [], currentEngagements = [], onClose, onDone }) => {
  const [options, setOptions] = useState({});
  const [engagementIds, setEngagementIds] = useState(
    (request.amendments && request.amendments.length > 0
      ? request.amendments.map((a) => a.engagementId)
      : [request.engagementId]).filter(Boolean)
  );
  const [tabs, setTabs] = useState({});   // { [id]: { baseline, fields } }
  const [activeId, setActiveId] = useState(null);
  const [reason, setReason] = useState(request.reason || '');
  const [reviewerStaffId, setReviewerStaffId] = useState(
    reviewerPickOf(request)
  );
  // A named reviewer no longer on the roster is cleared and must be re-picked.
  useClearOffRosterPick(reviewerStaffId, setReviewerStaffId, null, 'master');
  const [files, setFiles] = useState([]);
  const { removeIds, toggle, appendTo } = useRemovableFiles();
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  // The request's Screening section (after the Reviewer; the question only with a QM reviewer).
  const [screening, setScreening] = useState(() => earScreeningFromPayload(request.payload));
  const [screeningFile, setScreeningFile] = useState(null);
  const setScreeningField = (k, v) => setScreening((prev) => ({ ...prev, [k]: v }));
  const screeningAsked = useIsQmReviewer(reviewerStaffId);
  const screeningDoc = (request.files || []).find(isScreeningDoc) || null;

  const engOf = useCallback((id) =>
    currentEngagements.find((e) => e.id === id)
    || (options.engagements || []).find((e) => e.id === id)
    || null, [currentEngagements, options]);
  // 公司名；同公司多案時加服務／描述（頁籤第二行、句子裡的括號）。
  const tabLabels = engagementLabelsOf(engagementIds.map(engOf));
  const tabNameOf = (id) => (tabLabels[id] || {}).text || `#${id}`;

  // Baseline = the LIVE engagement (in the master's value shape); prefill = the
  // live engagement with this request's already-requested afters laid on top.
  // Both conversions live in WorkflowEarNew so the new-request page and this
  // modal can never drift apart.
  useEffect(() => {
    (async () => {
      let opts = {};
      try {
        opts = await fetchJSON('/api/workflow/ear/options');
        setOptions(opts);
      } catch (e) { setError(e.message); }
      const amendments = (request.amendments && request.amendments.length > 0)
        ? request.amendments
        : [{ engagementId: request.engagementId, payload: request.payload || {} }];
      const next = {};
      for (const a of amendments) {
        const live = currentEngagements.find((e) => e.id === a.engagementId)
          || (opts.engagements || []).find((e) => e.id === a.engagementId);
        if (!live) continue;
        next[a.engagementId] = {
          baseline: engagementToEarValues(live),
          fields: earPrefillFields(live, a.payload, opts),
        };
      }
      setTabs(next);
      setActiveId((cur) => (cur && next[cur] ? cur : (Object.keys(next)[0] ? Number(Object.keys(next)[0]) : null)));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const patchTab = (id, part) => setTabs((prev) => ({ ...prev, [id]: { ...prev[id], ...part } }));
  const updateSelection = (ids) => {
    setError(null);
    setEngagementIds(ids);
    setTabs((prev) => {
      const next = {};
      for (const id of ids) {
        if (prev[id]) { next[id] = prev[id]; continue; }
        const live = engOf(id);
        if (!live) continue;
        next[id] = { baseline: engagementToEarValues(live), fields: earPrefillFields(live, null, options) };
      }
      return next;
    });
    setActiveId((cur) => (ids.includes(cur) ? cur : (ids[0] || null)));
  };

  const changesOf = (id) => {
    const t = tabs[id];
    return t ? diffEarFields(t.baseline, t.fields, options) : {};
  };

  // 「讓 B 跟 A 一模一樣」——抄來源頁籤目前的整份內容（與 New 頁同一顆
  // helper；不必先編輯過來源）。
  const [copyNote, setCopyNote] = useState(null);
  const copySources = engagementIds
    .filter((eid) => eid !== activeId && tabs[eid])
    .map((eid) => ({
      id: eid,
      label: tabNameOf(eid),
      badge: Object.keys(changesOf(eid)).length,
    }));
  const copyFrom = (srcId) => {
    const src = tabs[srcId];
    const dst = tabs[activeId];
    if (!src || !dst) return;
    const copied = copyEarFields(src, dst.fields, { feesLocked: !!(engOf(activeId) || {}).qcRecordId });
    patchTab(activeId, { fields: copied.fields });
    setCopyNote(`This tab now matches ${tabNameOf(srcId)}'s values.`);
  };
  useEffect(() => { setCopyNote(null); }, [activeId]);

  // The blanks this modal can have — the coverage, the reason and the
  // reviewer. "No changes made yet" is a rule about the change set and stays a
  // banner error.
  const gaps = useMemo(() => {
    const g = [];
    if (engagementIds.length === 0) g.push({ key: 'engagementIds', label: 'Engagement(s)' });
    if (!reason.trim()) g.push({ key: 'reason', label: 'Reason' });
    if (!reviewerStaffId) g.push({ key: 'reviewerName', label: 'Reviewer' });
    g.push(...earScreeningGaps(screening, screeningAsked));
    return g;
  }, [engagementIds, reason, reviewerStaffId, screening, screeningAsked]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    setError(null);
    if (!validation.validate()) return;
    const clash = earTabsSlotConflict(engagementIds, tabs, engOf, options);
    if (clash) { setError(clash.message); return; }
    const dupTab = engagementIds.find((id) => ((tabs[id] || {}).duplicates || []).length > 0);
    if (dupTab) { setError(tabs[dupTab].duplicates[0].message); return; }
    const amendments = engagementIds
      .filter((id) => tabs[id])
      .map((id) => ({ engagementId: id, changes: changesOf(id) }));
    const anyChange = amendments.some((a) => Object.keys(a.changes).length > 0);
    const earKept = (request.files || []).filter((f) => !removeIds.includes(f.id));
    const screeningAnswered = screeningAsked ? screening.screeningRequired !== '' : !!(screeningFile || screeningDoc);
    if (!anyChange && !earKept.some((f) => f.isAttachmentUpdate) && files.length === 0 && !screeningAnswered) {
      setError('No changes made yet — edit a field.'); return;
    }
    const form = new FormData();
    form.set('reason', reason.trim());
    form.set('reviewerStaffId', reviewerStaffId);
    form.set('amendments', JSON.stringify(amendments));
    files.forEach(f => form.append('files', f));
    appendTo(form);
    appendEarScreening(form, screening, screeningFile, screeningAsked);

    setSaving(true);
    try {
      await fetchJSON(`/api/workflow/ear/${request.id}`, { method: 'PATCH', body: form });
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  const active = activeId ? tabs[activeId] : null;
  const activeEng = activeId ? engOf(activeId) : null;

  return (
    <FormModal
      title={`Modify ${request.requestNo}`}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      )}
    >
      <ReturnCommentsBanner events={events} />
      <FormValidationProvider value={validation.providerValue}>
        <GroupedEngagementPicker
          engagements={options.engagements || []}
          selectedIds={engagementIds}
          onChange={updateSelection}
        />
        <FieldInvalidInline field="engagementIds" text="Required — pick at least one engagement" />
        {engagementIds.length > 1 && (
          <>
            <EarPerTabHint count={engagementIds.length} />
            <EarTabBar
              items={engagementIds.map((id) => ({
                id,
                label: (tabLabels[id] || {}).label || `#${id}`,
                sub: (tabLabels[id] || {}).sub,
                title: (engOf(id) || {}).engagementName,
                badge: Object.keys(changesOf(id)).length,
              }))}
              activeId={activeId}
              onSelect={setActiveId}
            />
          </>
        )}
        {engagementIds.length > 1 && (
          <EarCopyRow sources={copySources} onCopy={copyFrom} />
        )}
        {copyNote && <p className="text-xs text-emerald-700">{copyNote}</p>}
        <EarSlotConflictNotice conflict={earTabsSlotConflict(engagementIds, tabs, engOf, options)} />
        {active && activeEng && (
          <EarFieldsEditor
            key={activeId}
            fields={active.fields}
            setFields={(updater) => patchTab(activeId, { fields: typeof updater === 'function' ? updater(active.fields) : updater })}
            options={options}
            currentName={activeEng.engagementName}
            engagement={activeEng}
            onDuplicates={(duplicates) => patchTab(activeId, { duplicates })}
            // Reviewer comments are keyed exactly as the detail page keys them —
            // `<engagementId>:<field>` on a multi-engagement request, the bare
            // field on a single one — so each comment surfaces on its own tab,
            // under the control it talks about.
            anchor={(k) => ((request.amendments || []).length > 1 ? `${activeId}:${k}` : k)}
          />
        )}
        <Field label="Reason" required field="reason">
          <textarea rows="2" value={reason} onChange={e => setReason(e.target.value)} className={inputCls} />
        </Field>
        <div>
          <EditableAttachments
            files={(request.files || []).filter((f) => !isScreeningDoc(f))}
            removeIds={removeIds}
            onToggle={toggle}
            downloadHref={(f) => `${API_BASE_URL}/api/workflow/ear/${request.id}/files/${f.id}`}
            badgeFor={(f) => (f.isAttachmentUpdate
              ? ['Attachment update', 'bg-amber-50 text-amber-700']
              : ['Supporting', 'bg-slate-100 text-slate-600'])}
            label="Attachments"
          />
          <FieldCommentInline field="files" />
          {(request.files || []).length === 0 && (
            <p className="mb-2 text-xs text-slate-500">No files attached yet.</p>
          )}
          <label className="block text-xs font-medium text-slate-500 mt-2 mb-1.5">Add More Files</label>
          <FilePicker
            multiple
            accept=".pdf,.doc,.docx"
            onChange={e => setFiles(Array.from(e.target.files || []))}
          />
        </div>
        <Field label="Reviewer" required field="reviewerName" hint={ENGREG_REVIEWER_HINT}>
          <ReviewerSelect roster="master" value={reviewerStaffId} onChange={setReviewerStaffId} />
        </Field>
        {/* After the Reviewer, like Registration: the question only with a QM
            reviewer. Without one only the upload shows, and saving drops a
            screening answer already on the request; say so. */}
        {!screeningAsked && reviewerStaffId && request.payload?.screening && typeof request.payload.screening.required === 'boolean' && (
          <p className="text-sm text-amber-700">
            The reviewer is not from the QM department, so QM does not perform the screening — saving removes the screening answer; an attached screening document is kept.
          </p>
        )}
        {reviewerStaffId && (
          <SubSection title="Screening" hint={screeningAsked ? EAR_SCREENING_SECTION_HINT : EAR_SCREENING_DOC_ONLY_HINT}>
            <ScreeningUpload
              f={screening} set={setScreeningField}
              screeningFile={screeningFile} setScreeningFile={setScreeningFile}
              existing={(!screeningAsked || screening.screeningRequired === 'no') && screeningDoc ? [screeningDoc] : []}
              anchor={(k) => k} required={false} blankNote={EAR_SCREENING_BLANK_NOTE}
              docOnly={!screeningAsked}
              currentBlock={screeningAsked && screeningDoc && screening.screeningRequired !== 'no' ? (
                <p className="text-sm text-amber-700">
                  Saving removes the attached screening document — it is kept only with a No answer.
                </p>
              ) : null}
            />
          </SubSection>
        )}
      </FormValidationProvider>
      {(error || validation.error) && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <span>{error || validation.error}</span>
        </div>
      )}
    </FormModal>
  );
};

export default WorkflowEarDetail;
